import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { Segmented } from "@a2z/ui";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  SMART_BUCKETS_ORDERED,
  SMART_BUCKET_TONE,
  type SmartBucketId,
} from "@/modules/orders/lib/smart-buckets";
import type {
  OrdersBucketFilter,
  OrdersSort,
} from "@/modules/orders/components/OrdersHeroFilters";
import type { PaymentFilter } from "@/modules/orders/components/OrdersFiltersDrawer";

type TableViewMode = "table" | "cards";

type Props = {
  filter: OrdersBucketFilter;
  onFilterChange: (next: OrdersBucketFilter) => void;
  counts: Record<SmartBucketId | "all" | "incomplete", number>;
  payment: PaymentFilter;
  onPaymentChange: (next: PaymentFilter) => void;
  sort: OrdersSort;
  onSortChange: (next: OrdersSort) => void;
  viewMode: TableViewMode;
  onViewModeChange: (next: TableViewMode) => void;
};

/**
 * The orders-card filter toolbar (Trioty design): status pills (bucket tabs) +
 * a Payment filter + a Table/Card view toggle + a Sort control. Sorting by
 * total is in the design but unsupported by the API, so those options are
 * disabled + flagged.
 */
export function OrdersFilterToolbar({
  filter,
  onFilterChange,
  counts,
  payment,
  onPaymentChange,
  sort,
  onSortChange,
  viewMode,
  onViewModeChange,
}: Props) {
  const t = useTranslations();
  const locale = useLocale();

  const pills: {
    value: OrdersBucketFilter;
    label: string;
    dot?: string;
    count: number;
  }[] = [
    { value: "all", label: t(keys.common.all), count: counts.all },
    // needs_attention + waiting_on_buyer live in the always-on sections at the
    // top of the page, so they're omitted from the stage-bucket tabs here.
    ...SMART_BUCKETS_ORDERED.filter(
      (bucket) => bucket !== "needs_attention" && bucket !== "waiting_on_buyer",
    ).map((bucket) => ({
      value: bucket as OrdersBucketFilter,
      label: t(SMART_BUCKET_TONE[bucket].labelKey),
      dot: SMART_BUCKET_TONE[bucket].dot,
      count: counts[bucket] ?? 0,
    })),
    {
      value: "incomplete" as OrdersBucketFilter,
      label: t(keys.orders.bucketIncomplete),
      count: counts.incomplete,
    },
  ];

  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      {/* Status pills */}
      <div className="no-scrollbar flex flex-wrap items-center gap-1 rounded-[13px] border border-[color:var(--line2)] bg-[color:var(--surface-alt)] p-1">
        {pills.map((pill) => {
          const active = filter === pill.value;
          return (
            <button
              key={pill.value}
              type="button"
              onClick={() => onFilterChange(pill.value)}
              aria-pressed={active}
              className={[
                "inline-flex items-center gap-[7px] whitespace-nowrap rounded-[9px] px-[13px] py-[7px] text-[13px] transition-colors",
                active
                  ? "bg-[color:var(--card)] font-bold text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(16,16,15,0.06)]"
                  : "font-medium text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]",
              ].join(" ")}
            >
              {pill.dot ? (
                <span className="h-[7px] w-[7px] rounded-full" style={{ background: pill.dot }} />
              ) : null}
              {pill.label}
              <span
                className={[
                  "text-[11px] font-bold",
                  "text-[color:var(--text-muted)]",
                ].join(" ")}
              >
                {pill.count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Payment + view toggle + sort */}
      <div className="flex items-center gap-[9px]">
        {/* Payment filter */}
        <FilterChip
          icon={<FilterLinesIcon />}
          label={t(keys.orders.colPayment)}
          value={payment}
          onChange={(v) => onPaymentChange(v as PaymentFilter)}
          selectedText={
            payment === "cod"
              ? t(keys.orders.paymentMethodCod)
              : payment === "online"
                ? t(keys.orders.paymentMethodExternalOnline)
                : null
          }
          onClear={() => onPaymentChange("all")}
          rtl={locale === "ar"}
          options={[
            { value: "all", label: t(keys.common.all) },
            { value: "cod", label: t(keys.orders.paymentMethodCod) },
            { value: "online", label: t(keys.orders.paymentMethodExternalOnline) },
          ]}
        />

        {/* View toggle (Table / Cards) — labeled Segmented, matching Products. */}
        <Segmented
          tone="light"
          value={viewMode}
          onChange={(next) => onViewModeChange(next)}
          options={[
            { value: "table", label: t(keys.orders.viewTable), icon: <TableIcon /> },
            { value: "cards", label: t(keys.orders.viewCards), icon: <GridIcon /> },
          ]}
          ariaLabel={t(keys.orders.viewModeAria)}
        />

        {/* Sort */}
        <FilterChip
          icon={<SortIcon />}
          label={t(keys.orders.sortLabel)}
          value={sort}
          onChange={(v) => {
            if (v === "newest" || v === "oldest") onSortChange(v);
          }}
          selectedText={sort === "oldest" ? t(keys.orders.sortOldest) : t(keys.orders.sortNewest)}
          alwaysShowValue
          rtl={locale === "ar"}
          options={[
            { value: "newest", label: t(keys.orders.sortNewest) },
            { value: "oldest", label: t(keys.orders.sortOldest) },
          ]}
        />
      </div>
    </div>
  );
}

/**
 * A design filter "chip" built on the app's reusable dropdown menu
 * (@headlessui/react `Menu`) — same pattern used across the app (e.g. the
 * coupons table). Shows the icon + label + selected value, an optional clear
 * (✕), and a styled dropdown of options with a check on the active one.
 */
function FilterChip({
  icon,
  label,
  value,
  onChange,
  selectedText,
  options,
  rtl,
  onClear,
  alwaysShowValue = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
  selectedText: string | null;
  options: { value: string; label: string }[];
  rtl: boolean;
  onClear?: () => void;
  alwaysShowValue?: boolean;
}) {
  const active = selectedText !== null;
  const showValue = alwaysShowValue || active;
  return (
    <Menu as="div" className="relative">
      <MenuButton
        className="inline-flex items-center gap-2 rounded-[11px] border bg-[color:var(--card)] px-3 py-2 text-[13px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)] focus:outline-none"
        style={{ borderColor: active ? "var(--accent-500)" : "var(--border-light)" }}
      >
        {icon}
        <span>{label}</span>
        {showValue ? (
          <>
            {active ? (
              <span aria-hidden="true" className="h-[14px] w-px bg-[color:var(--line2)]" />
            ) : null}
            <span className="font-bold text-[color:var(--text-primary)]">
              {selectedText ?? ""}
            </span>
          </>
        ) : null}
        {active && onClear ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={`${label} ✕`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClear();
            }}
            className="inline-flex text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-secondary)]"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </span>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-disabled)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        dir={rtl ? "rtl" : "ltr"}
        className="z-50 mt-1.5 min-w-[184px] overflow-hidden rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--card)] p-1 shadow-[var(--shadow-md)] focus:outline-none"
      >
        {options.map((o) => {
          const selected = o.value === value;
          return (
            <MenuItem key={o.value}>
              <button
                type="button"
                onClick={() => onChange(o.value)}
                className="flex w-full items-center justify-between gap-3 rounded-[8px] px-3 py-2 text-start text-[13px] text-[color:var(--text-secondary)] data-[focus]:bg-[color:var(--soft)]"
              >
                <span className={selected ? "font-bold text-[color:var(--text-primary)]" : ""}>
                  {o.label}
                </span>
                {selected ? (
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--accent-600)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                ) : null}
              </button>
            </MenuItem>
          );
        })}
      </MenuItems>
    </Menu>
  );
}

function FilterLinesIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 5h18M6 12h12M10 19h4" />
    </svg>
  );
}
function SortIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 5h10M11 9h7M11 13h4M3 17l3 3 3-3M6 18V4" />
    </svg>
  );
}
function TableIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 5h18M3 9h18M3 15h18M3 19h18" />
    </svg>
  );
}
function GridIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
    </svg>
  );
}
